import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedArray } from '@ember/reactive/collections';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { copyObject, stringifyAlphabetical } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import type { PreviewItemsData } from 'webapp/components/scripts/edit';
import ScriptsFormCodeLineComponent from 'webapp/components/scripts/form/code-line';

import DateRange from 'webapp/models-custom/date-range';
import type { ExpressionDetailItem } from 'webapp/models-custom/script';
import type Script from 'webapp/models-custom/script';
import ScriptItem from 'webapp/models-custom/script-item';

interface Args {
	model: Script;
	previewItemsData?: PreviewItemsData;
	defaultDateRange?: DateRange;
	updateExpression: (expression: string, expressionDetails: ExpressionDetailItem[]) => void;
}

export default class ScriptsFormCodeComponent extends Component<Args> {
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;

	placeholderAvailableLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

	@tracked itemsPart1: ScriptItem[];
	@tracked itemsPart2: ScriptItem[];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const { itemsPart1, itemsPart2 } = this.mmScriptsService.createScriptItems(
			this.args.model.expression,
			this.args.model.expressionDetails,
		);

		this.itemsPart1 = trackedArray(itemsPart1);
		this.itemsPart2 = trackedArray(itemsPart2);
	}

	private _allMetricItems: ScriptItem[] = [];

	get allMetricItems() {
		const items = this.itemsPart1.filter((item) => item.isMk);
		items.push(...this.itemsPart2.filter((item) => item.isMk));

		// check if any has been added - if yes, that one needs its dateRange updated from the "master"
		// only do the checking if this is not the first get
		if (this._allMetricItems) {
			const itemsToChange = items.filter((item) => {
				return (
					!this._allMetricItems.includes(item) ||
					!item.dataSelect?.dateRange ||
					DateRange.isRangeFormulaEmpty(item.dataSelect.dateRange.range)
				);
			});

			next(() => {
				this.updateDateRangesInDsObjs(itemsToChange);
			});
		}

		// eslint-disable-next-line ember/no-side-effects
		this._allMetricItems = items;

		return items;
	}

	get allScriptItems() {
		return [...this.itemsPart1, ...this.itemsPart2];
	}

	@cached
	get allScriptItemValueHash() {
		return hashCode(JSON.stringify(this.allScriptItems.map((i) => i.valueHash)));
	}

	get metricScriptItems() {
		return this.allScriptItems.filter((item) => item.isMetric);
	}

	get isMksTimezoneWarningShown() {
		const usedTimezones = this.args.model.usedTimezones;

		const uniqueOffsets = usedTimezones.reduce((uniqueOffsets, timezone) => {
			const offset = timezone.gmtOffset;

			if (offset && !uniqueOffsets.includes(offset)) {
				uniqueOffsets.push(offset);
			}

			return uniqueOffsets;
		}, [] as string[]);

		return uniqueOffsets.length > 1;
	}

	get nextPlaceholderLetter() {
		const usedPlaceholdersCount = this.metricScriptItems.length;

		return this.placeholderAvailableLetters[usedPlaceholdersCount]!;
	}

	updatePlaceholderLetters() {
		let nextPlaceholderIndex = this.metricScriptItems.filter(
			(item) => !item.isPlaceholder,
		).length;

		this.itemsPart1.forEach((item) => {
			if (item.isPlaceholder) {
				item.value = this.placeholderAvailableLetters[nextPlaceholderIndex]!;

				nextPlaceholderIndex++;
			}
		});

		this.itemsPart2.forEach((item) => {
			if (item.isPlaceholder) {
				item.value = this.placeholderAvailableLetters[nextPlaceholderIndex]!;

				nextPlaceholderIndex++;
			}
		});
	}

	@action
	updateCode() {
		const { expression, expressionDetails } = this.mmScriptsService.getExpressionDataFromItems(
			this.itemsPart1,
			this.itemsPart2,
		);

		if (
			expression !== this.args.model.expression ||
			!this.equalExpressionDetails(expressionDetails, this.args.model.expressionDetails)
		) {
			this.args.updateExpression(expression as string, expressionDetails);

			// trigger allMetricItems getter to update and ensure that date ranges are updated in dataSelects
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.allMetricItems;
		}
	}

	@action
	updateDateRangesInDsObjs(items = this.allMetricItems) {
		const defaultDateRangeFormula = this.args.defaultDateRange?.formula;
		if (
			defaultDateRangeFormula &&
			!DateRange.isRangeFormulaEmpty(defaultDateRangeFormula.range)
		) {
			// what we want to do here is to already show the preview interval when opening advanced popup
			items.forEach((scriptItem) => {
				scriptItem.updateDateRangeFromFormula(defaultDateRangeFormula);
			});
		}
	}

	@action
	scheduleUpdateDateRangesInDsObjs() {
		once(this, this.updateDateRangesInDsObjs);
	}

	@action
	scheduleCodeUpdate() {
		next(this, this.updateCode); // script-code-item.updateValue must trigger first
	}

	@action
	updatePreviewStrings() {
		const previewItemsData = this.args.previewItemsData || {};
		[...this.itemsPart1, ...this.itemsPart2].forEach((item) => {
			if (item.id) {
				const previewItemData = previewItemsData[item.id];
				if (previewItemData) {
					item.previewString = previewItemData.previewString;
					item.queriedValueHash = previewItemData.queriedValueHash;
				} else {
					item.previewString = '';
					item.queriedValueHash = 0; // requery / show loader again next time
				}
			}
		});
	}

	@action
	checkPlaceholders() {
		this.updateCode();
		this.mmScriptsService.addScriptItemPlaceholders(this.itemsPart1, this.itemsPart2);

		this.updatePlaceholderLetters();
	}

	@action
	addItem(index: number, item: ScriptItem) {
		if (index === 1) {
			this.itemsPart1 = [...this.itemsPart1, item];
		} else {
			this.itemsPart2 = [...this.itemsPart2, item];
		}
	}

	@action
	removeItem(index: number, item: ScriptItem) {
		if (index === 1) {
			this.itemsPart1 = this.itemsPart1.filter((i) => i !== item);
		} else {
			this.itemsPart2 = this.itemsPart2.filter((i) => i !== item);
		}
	}

	@action
	onBracketChange(index: number, item: ScriptItem, isSelect: boolean) {
		const model = index === 1 ? this.itemsPart1 : this.itemsPart2;

		// this modifies the model items
		item.bracketChange(model, isSelect);

		ScriptItem.setSelectedBrackets(model);

		// updating items to rerender
		if (index === 1) {
			this.itemsPart1 = this.itemsPart1;
		} else {
			this.itemsPart2 = this.itemsPart2;
		}
	}

	override willDestroy() {
		super.willDestroy();

		// cleanup current items
		(this.itemsPart1 || []).forEach((item) => {
			item.cleanup();
		});
		(this.itemsPart2 || []).forEach((item) => {
			item.cleanup();
		});
	}

	private equalExpressionDetails(a: ExpressionDetailItem[], b: ExpressionDetailItem[]): boolean {
		if (a === b) return true;
		if (a?.length !== b?.length) return false;

		const normalizeForComparison = (item: ExpressionDetailItem) => {
			// the stored details can carry a metricSetup filters key
			const copy: { id: string; metricSetup?: DSSetup.MetricSetupData } = copyObject(item);
			// Remove filters key if null or undefined so they compare as equivalent
			if (copy.metricSetup && !copy.metricSetup.filters) {
				delete copy.metricSetup.filters;
			}
			return copy;
		};

		const normalizedA = a.map(normalizeForComparison).sort((x, y) => x.id.localeCompare(y.id));
		const normalizedB = b.map(normalizeForComparison).sort((x, y) => x.id.localeCompare(y.id));

		return stringifyAlphabetical(normalizedA) === stringifyAlphabetical(normalizedB);
	}
	<template>
		<div
			id="scriptCode"
			data-hash={{this.allScriptItemValueHash}}
			{{didUpdate this.updatePreviewStrings @previewItemsData}}
			{{didUpdate this.scheduleUpdateDateRangesInDsObjs @defaultDateRange.formula}}
			{{didUpdate this.scheduleCodeUpdate this.allScriptItemValueHash}}
		>

			<ScriptsFormCodeLineComponent
				@type="above"
				@model={{this.itemsPart1}}
				@nextPlaceholderLetter={{this.nextPlaceholderLetter}}
				@isMksTimezoneWarningShown={{this.isMksTimezoneWarningShown}}
				@onMetricDelete={{this.checkPlaceholders}}
				@addItem={{fn this.addItem 1}}
				@removeItem={{fn this.removeItem 1}}
				@onBracketChange={{fn this.onBracketChange 1}}
			/>

			<div class="divisionLine"></div>

			<ScriptsFormCodeLineComponent
				@type="below"
				@model={{this.itemsPart2}}
				@nextPlaceholderLetter={{this.nextPlaceholderLetter}}
				@isMksTimezoneWarningShown={{this.isMksTimezoneWarningShown}}
				@onMetricDelete={{this.checkPlaceholders}}
				@addItem={{fn this.addItem 2}}
				@removeItem={{fn this.removeItem 2}}
				@onBracketChange={{fn this.onBracketChange 2}}
			/>

		</div>
	</template>
}
